import React, { useCallback } from "react"
import { Link } from "gatsby"
import { ChevronRightIcon } from "components/Icons"
import Show from "components/Show"
import Spinner from "components/Spinner"
import type { Notification } from "hooks/useNotifications"
import useNotifications from "hooks/useNotifications"
import useSession from "hooks/useSession"
import { getRelativeTimeString } from "lib/helpers"
import {
  getNotificationLinkText,
  getNotificationLinkUrl
} from "lib/notifications"

export interface NotificationsListProps {
  notifications?: Notification[]
}

const NotificationsList = ({ notifications }: NotificationsListProps) => {
  const { session } = useSession()
  const { dismissNotification } = useNotifications()
  const onLinkClick = useCallback(
    async (id: number) => {
      try {
        await dismissNotification(id)
      } catch {
        /* Empty */
      }
    },
    [dismissNotification]
  )

  if (!notifications) {
    return (
      <div className="flex justify-center p-6">
        <Spinner width={50} height={50} />
      </div>
    )
  }

  if (!notifications.length) {
    return <p>You have no notifications available yet.</p>
  }

  return (
    <ul className="bg-white border border-primary-light-gray rounded-lg px-4">
      {notifications.map(notification => {
        const url = getNotificationLinkUrl(notification)
        const text = getNotificationLinkText(notification)

        return (
          <li
            key={notification.id}
            className="flex justify-between items-center border-t border-primary-light-gray py-4 first:border-t-0"
          >
            <div>
              <h3 className="font-medium">{notification.message}</h3>

              <p className="text-sm text-primary-dark-gray">
                {getRelativeTimeString(new Date(notification.createdAt))}
              </p>
            </div>

            <Show when={notification.requiresAction}>
              {url.startsWith(`/dashboard/`) ? (
                <form
                  action={`/dashboard/notification/${notification.id}/`}
                  method="POST"
                >
                  <input
                    type="hidden"
                    name="csrfmiddlewaretoken"
                    value={session?.csrf}
                  />

                  <button
                    type="submit"
                    data-testid="view-order"
                    className="flex items-center text-sm font-medium disabled:opacity-80"
                    disabled={!session?.csrf}
                  >
                    <span className="underline">{text}</span>
                    <ChevronRightIcon className="w-4 h-4" aria-hidden="true" />
                  </button>
                </form>
              ) : (
                <Link
                  to={url}
                  className="flex items-center text-sm font-medium"
                  onClick={() => onLinkClick(notification.id)}
                >
                  <span className="underline">{text}</span>
                  <ChevronRightIcon className="w-4 h-4" aria-hidden="true" />
                </Link>
              )}
            </Show>
          </li>
        )
      })}
    </ul>
  )
}

export default NotificationsList
